/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import type { HTMLAttributes } from 'react';
import {
  getInitialRecord,
  prepareRecordsForSave,
  TParsedFieldWithFieldName,
} from 'containers/Base/utilities';
import { baseDialogStyle } from 'containers/Base/styles';
import type { TSchema } from 'types/items';
import type { WorkingState } from '../../Data/data.types';
import RecordProperties from '../components/RecordProperties';

export interface NewDialogProps<T extends any[] = any[]>
  extends HTMLAttributes<HTMLDivElement> {
  fields: TParsedFieldWithFieldName[];
  handleAdd: (newRecords: T) => void;
  handleCancel: () => void;
  itemLabel?: string;
  recordTemplate?: T[number];
  open: boolean;
  schema: TSchema;
  workingState: WorkingState;
}

export type NewDialogComponent<T extends any[] = any[]> = FC<NewDialogProps<T>>;

const NewDialog: NewDialogComponent = ({
  fields,
  handleAdd,
  handleCancel,
  itemLabel,
  recordTemplate,
  open,
  schema,
  workingState,
}) => {
  const { classes } = baseDialogStyle();

  // State
  const [newRecord, setNewRecord] = useState<any>({});
  const [saving, setSaving] = useState(false);

  // Handlers
  const handleSetInitialRecord = () => {
    // Add an initial empty record based on the fields
    const initialRecord = getInitialRecord(fields);

    // If a record template is provided, merge it with the initial record
    if (recordTemplate) {
      setNewRecord({ ...initialRecord, ...recordTemplate });
    } else {
      setNewRecord(initialRecord);
    }
  };
  const onAddRecord = () => {
    const preparedNewRecords = prepareRecordsForSave(fields, schema, [
      newRecord,
    ]);
    // If a recordTemplate was provided, merge any properties provided in that template not
    // already present in preparedNewRecord.  This is required because we may have hidden some
    // of the fields if they were part on an inherentFilter.
    if (recordTemplate && preparedNewRecords.length > 0) {
      Object.keys(recordTemplate).forEach((key) => {
        if (!(key in preparedNewRecords[0])) {
          preparedNewRecords[0][key] = recordTemplate[key];
        }
      });
    }

    setSaving(true);
    handleAdd(preparedNewRecords);
  };
  const onCancel = () => {
    handleCancel();
    handleSetInitialRecord();
  };

  // Hooks
  useEffect(() => {
    handleSetInitialRecord();
  }, [fields]);
  useMemo(() => {
    if (!workingState?.adding && !workingState?.error && saving) {
      handleSetInitialRecord();
      setSaving(false);
    }
  }, [workingState]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="new-record-dialog-title"
      aria-describedby="new-record-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">New {itemLabel}</DialogTitle>
      <DialogContent>
        <RecordProperties
          fields={fields}
          mode="add"
          record={newRecord}
          schema={schema}
          setRecord={setNewRecord}
          workingState={workingState}
        />
        <div className={classes.progressContainer}>
          {workingState?.adding && (
            <div className={classes.progressInner}>
              <CircularProgress size={25} />
              <span className={classes.progressLabel}>
                Adding {itemLabel}...
              </span>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onAddRecord}
          color="primary"
          disabled={
            workingState?.adding ||
            fields.some(
              (f) =>
                f.required === 'Y' &&
                f.type !== 'bool' &&
                f.fieldName &&
                !newRecord[f.fieldName]
            )
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewDialog;
